import { useMemo } from 'react';
import { Button } from 'src/v2/commonComponents/Button';
import { CanonicalInterval, GTMEvent, PlanFamily, PlanKind } from 'src/types';
import { sendGTMEvent } from 'src/utils';
import { usePaymentControlActions, usePaymentControlState } from 'src/hooks';
import { PLAN_KIND_TITLE_MAP_V3, SVG_SIZE_M } from 'src/constants';
import styles from './GetPlanButton.module.scss';
import { CheckCircle } from '@phosphor-icons/react';
import { NinjaSubscriptionPlan } from 'src/types/wallet/models/NinjaSubscriptionPlan';

export interface GetPlanButtonV2Props {
  isCurrentPlan: boolean;
  subscribedTier?: NinjaSubscriptionPlan;
  planKind: PlanKind;
  planFamily: PlanFamily;
  onOpenCheckout: () => void;
}

export const GetPlanButton = ({
  isCurrentPlan,
  subscribedTier,
  planKind,
  planFamily,
  onOpenCheckout,
}: GetPlanButtonV2Props) => {
  const { onSetSelectedFamily, onSetSelectedPlan } = usePaymentControlActions();
  const { selectedPeriod } = usePaymentControlState();

  const buttonLabel = useMemo(() => {
    if (isCurrentPlan) {
      return (
        <span className={styles.currentButtonLabel}>
          <CheckCircle size={SVG_SIZE_M} weight="fill" />{' '}
          <span>Current plan</span>
        </span>
      );
    }

    if (!!subscribedTier) {
      return 'Change plan';
    }

    return `Get ${PLAN_KIND_TITLE_MAP_V3[planKind]?.fullTitle || ''}`;
  }, [isCurrentPlan, planKind, subscribedTier]);

  const getMonthlyGTMEvent = () => {
    switch (planFamily) {
      case PlanFamily.PRO:
        return GTMEvent.GET_PRO_MONTHLY_CLICK;
      case PlanFamily.MAX:
        return GTMEvent.GET_MAX_MONTHLY_CLICK;
      case PlanFamily.BUSINESS:
      default:
        return GTMEvent.GET_BUSINESS_MONTHLY_CLICK;
    }
  };

  const getAnnualGTMEvent = () => {
    switch (planFamily) {
      case PlanFamily.PRO:
        return GTMEvent.GET_PRO_ANNUAL_CLICK;
      case PlanFamily.MAX:
        return GTMEvent.GET_MAX_ANNUAL_CLICK;
      case PlanFamily.BUSINESS:
      default:
        return GTMEvent.GET_BUSINESS_MONTHLY_CLICK;
    }
  };

  const handleButtonClick = () => {
    const gtmEvent =
      selectedPeriod === CanonicalInterval.MONTHLY
        ? getMonthlyGTMEvent()
        : getAnnualGTMEvent();

    sendGTMEvent(gtmEvent);
    onSetSelectedFamily(planFamily);
    onSetSelectedPlan({ planKind: planKind });

    onOpenCheckout();
  };

  return (
    <Button
      fullWidth
      color={isCurrentPlan ? 'secondary-outline' : 'highlight'}
      onClick={handleButtonClick}
      id="Plan-card-upgrade"
      className={isCurrentPlan ? styles.currentButton : ''}
    >
      {buttonLabel}
    </Button>
  );
};
